import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import '../EmployeeSurvey.css';

interface SurveyPasswordGateProps {
  children: (clearPassword: () => void) => React.ReactNode;
}

const STORAGE_KEY = 'ss_survey_password';

export const getSurveyPassword = (): string | null =>
  typeof window !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) : null;

export const clearSurveyPassword = (): void => {
  if (typeof window !== 'undefined') sessionStorage.removeItem(STORAGE_KEY);
};

/**
 * Shared-password gate for the employee survey submit page.
 *
 * Budtenders don't have Google accounts, so submissions are gated by a
 * single password distributed by managers rather than Firebase auth. The
 * password is held in sessionStorage (cleared on tab close); the actual
 * correctness check happens server-side on first submit. If the backend
 * returns 401, the form invokes `clearPassword` to kick the user back
 * here with an error.
 */
const SurveyPasswordGate: React.FC<SurveyPasswordGateProps> = ({ children }) => {
  const [password, setPassword] = useState<string | null>(() => getSurveyPassword());
  const [input, setInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) {
      setError('Please enter the survey password.');
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, trimmed);
    setPassword(trimmed);
    setError(null);
  };

  const clearPassword = () => {
    clearSurveyPassword();
    setPassword(null);
    setInput('');
    setError('That password was not accepted. Please try again.');
  };

  if (password) return <>{children(clearPassword)}</>;

  return (
    <div className="employee-survey">
      <Helmet>
        <title>Sweetspot — Employee Engagement Survey</title>
        <meta name="robots" content="noindex, nofollow" />
        <link
          href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,400&family=Work+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div className="lbg"><div className="sh s1" /><div className="sh s2" /><div className="sh s3" /></div>
      <div className="ww">
        <div className="wel">
          <div className="hb">Employee Survey</div>
          <h1>Enter the survey password</h1>
          <p className="sub">
            Your manager shared a password for this survey. Enter it below to begin. Your responses remain anonymous —
            we don't store your name or any other identifier alongside your answers.
          </p>
          <div className="anon-note" style={{ textAlign: 'left' }}>
            <span className="lock">🔒</span>
            <span>The password just confirms you're staff. It is not tied to your submission in any way.</span>
          </div>
          <form onSubmit={handleSubmit} className="sel-wrap" style={{ flexDirection: 'column', alignItems: 'stretch', maxWidth: 360, margin: '0 auto' }}>
            <input
              type="password"
              autoComplete="off"
              autoFocus
              placeholder="Survey password"
              value={input}
              onChange={e => setInput(e.target.value)}
              aria-label="Survey password"
            />
            <button type="submit" className="bbeg" style={{ marginTop: 14 }}>
              Continue
            </button>
          </form>
          {error && (
            <div className="submit-status error" style={{ marginTop: 14 }}>
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyPasswordGate;
